// src/pages/FAQPage.jsx
import { useEffect, useMemo, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { Link } from 'react-router-dom';
import SeoMeta from '../components/SeoMeta';
import { usePageMeta } from '../hooks/usePageMeta';

// ---- анимации
const float = keyframes`
  0%   { transform: translateY(0) }
  50%  { transform: translateY(-3px) }
  100% { transform: translateY(0) }
`;
const shimmer = keyframes`
  0% { background-position: 0% 50% }
  100% { background-position: 200% 50% }
`;

// ---- базовая разметка страницы
const Page = styled.section`
  padding: 48px 0 80px;
  .container{
    width: ${({theme})=>theme.container};
    margin: 0 auto;
    display: grid;
    grid-template-columns: 260px 1fr;
    gap: 18px;
  }
  @media (max-width: 980px){
    .container{ grid-template-columns: 1fr; }
  }
`;

const Head = styled.div`
  width: ${({theme})=>theme.container}; margin: 0 auto 18px;
  display:flex; align-items:end; justify-content:space-between; gap:16px; flex-wrap:wrap;
  .title-wrap h1{ margin:0 }
  .lead{ color:${({theme})=>theme.textMuted}; margin: 6px 0 0 }
`;

// ---- левый сайдбар
const Sidebar = styled.aside`
  position: sticky; top: 84px; align-self:start;
  border:1px solid ${({theme})=>theme.border};
  background:${({theme})=>theme.surface};
  border-radius:${({theme})=>theme.radius.lg}px; padding:12px;
  box-shadow:${({theme})=>theme.shadow.sm};
  @media (max-width: 980px){ position: static; }
`;

const NavLink = styled.a`
  display:flex; align-items:center; gap:10px; padding:10px 12px; border-radius:${({theme})=>theme.radius.md}px;
  text-decoration:none; color:inherit; border:1px solid transparent;
  &:hover{ background:${({theme})=>theme.body}; border-color:${({theme})=>theme.border}; }
  .dot{ width:8px; height:8px; border-radius:50%; background: ${({theme})=>theme.accent}; opacity:.6; animation:${float} 4s ease-in-out infinite; }
`;

// ---- поиск
const Toolbar = styled.div`
  display:flex; flex-wrap:wrap; gap:10px; margin-bottom:12px;
  input{
    flex:1 1 320px; min-width:220px;
    border:1px solid ${({theme})=>theme.border};
    background:${({theme})=>theme.surface};
    color:inherit; border-radius:${({theme})=>theme.radius.md}px;
    padding:10px 12px;
  }
  .btn{
    padding:10px 14px; border-radius:${({theme})=>theme.radius.md}px; cursor:pointer;
    border:1px solid ${({theme})=>theme.border}; background:${({theme})=>theme.body};
  }
`;

// ---- блоки FAQ
const Section = styled.section`
  scroll-margin-top: 84px; /* чтобы якоря прокручивались под fixed header */
  .kicker{ font-size:12px; text-transform:uppercase; letter-spacing:.12em; opacity:.7; }
  h2{ margin: 4px 0 12px; }
  & + & { margin-top: 28px; }
`;

const FaqGrid = styled.div`
  display:grid; gap:10px;
`;

const Q = styled.div`
  border:1px solid ${({theme})=>theme.border};
  background:${({theme})=>theme.surface};
  border-radius:${({theme})=>theme.radius.lg}px;
  overflow:hidden; box-shadow:${({theme})=>theme.shadow.sm};
  .btn{
    width:100%; text-align:left; padding:14px; background:none; border:none; cursor:pointer;
    display:flex; align-items:center; justify-content:space-between; gap:8px; font-weight:700;
  }
  .a{ padding:0 14px 14px; color:${({theme})=>theme.textMuted}; }
`;

const Badge = styled.span`
  display:inline-block; font-size:12px; padding:6px 10px; border-radius:${({theme})=>theme.radius.pill}px;
  border:1px dashed ${({theme})=>theme.border};
  background: linear-gradient(120deg, #ffffff10, #ffffff00 40%) padding-box,
              linear-gradient(90deg,#ff660055,#00c2ff55,#a8ff0055) border-box;
  background-size:200% 100%; animation:${shimmer} 6s linear infinite;
`;

// ---- мок иконки (простые inline svg)
const Icon = ({name, size=18})=>{
  const map = {
    cctv: <path d="M3 10h10l2 3H3v-3zm0-4h14v3H3V6zm16 6l2 2-4 4-2-2 4-4z" />,
    acs: <path d="M12 2a5 5 0 00-5 5v3H5v8h14v-8h-2V7a5 5 0 00-5-5zm-3 8V7a3 3 0 116 0v3H9z" />,
    gates: <path d="M3 7h4v10H3V7zm14 0h4v10h-4V7zM9 7h6v10H9V7z" />,
    electro: <path d="M13 2L4 14h6v8l9-12h-6V2z" />,
    water: <path d="M12 3l5 8a5 5 0 11-10 0l5-8z" />,
    smoke: <path d="M4 4h16v4H4V4zm0 6h12v4H4v-4zm14 0h2v10h-2V10z" />,
    vent: <path d="M12 3a9 9 0 100 18 9 9 0 000-18zm1 8h6a7 7 0 01-6 6v-6z" />,
    core: <path d="M4 12l8-8 8 8-8 8-8-8zm5-1l3 3 5-5" />,
    metal: <path d="M3 7h18v4H3V7zm0 6h18v4H3v-4z" />,
    wood: <path d="M4 20h16V8l-8-6-8 6v12z" />,
    paint: <path d="M4 10l6-6 6 6v8a2 2 0 01-2 2H6a2 2 0 01-2-2v-8z" />,
    delivery: <path d="M3 7h10v6H3V7zm10 2h4l3 3v3h-7V9zM6 18a2 2 0 110-4 2 2 0 010 4z" />
  };
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">{map[name]||map.metal}</svg>;
};

// ---- данные FAQ по разделам
const CATEGORIES = [
  {
    id: 'manufacturing',
    icon: 'metal',
    title: 'Металлообработка та виготовлення',
    items: [
      {
        q: 'Які формати креслень приймаєте?',
        a: 'DXF, DWG, STEP/IGES, а також PDF із розмірами. Якщо є 3D-модель — це прискорить оцінку та підготовку виробництва.'
      },
      {
        q: 'Які допуски забезпечуєте?',
        a: 'Для більшості деталей стандартні допуски IT10–IT13; для прецизійних — за домовленістю до IT7–IT8. Шорсткість до Ra 1.6 при чистовій обробці.'
      },
      {
        q: 'Терміни виготовлення?',
        a: 'Типово 5–20 робочих днів залежно від завантаження та складності. Терміново (експрес) — можливе за окремим погодженням.'
      },
      {
        q: 'Чи допомагаєте з підбором матеріалів?',
        a: 'Так. Порадимо сталь/сплав під навантаження, середовище та бюджет, а також варіанти термообробки та покриттів.'
      }
    ]
  },
  {
    id: 'furniture',
    icon: 'wood',
    title: 'Меблі, HPL та облицювання',
    items: [
      { q: 'З чого робите корпус?', a: 'МДФ/HDF, фанера, ДСП; облицювання HPL або шпон. Фасади — фарбовані, шпоновані або HPL-компакт.' },
      { q: 'Чи робите нестандартні розміри?', a: 'Так, усе під проєкт: індивідуальні габарити, фурнітура та інтеграція з інженерними системами (доступ, підсвітка).' },
      { q: 'Монтаж на об’єкті?', a: 'З власною бригадою, включно з доставкою, підйомом, пиловловлюванням та прибиранням після робіт.' }
    ]
  },
  {
    id: 'coatings',
    icon: 'paint',
    title: 'Покриття, зварювання, доставка',
    items: [
      { q: 'Які покриття доступні?', a: 'Грунт+емаль, порошкова фарба, цинкування (гаряче/гальванічне), нікелювання. Підбір системи — під умови експлуатації.' },
      { q: 'Чи виконуєте зварювання важких вузлів?', a: 'Так, зварні рами, балки, вали з наплавленням; НК/ВК за потреби.' },
      { q: 'Доставка по Україні?', a: 'Так. Власним транспортом або перевізником. Кейс-пакування/палети — щоб дійшло без пошкоджень.' }
    ]
  },
  {
    id: 'systems-cctv',
    icon: 'cctv',
    title: 'Відеоспостереження',
    items: [
      { q: 'Які камери ставите?', a: 'IP 2–8MP, PTZ, тепловізори, ANPR (розпізнавання номерів), з кольоровим нічним режимом і WDR.' },
      { q: 'Запис та зберігання?', a: 'NVR з RAID, резервування, шифрування. Можливий гібрид — локально + хмара. Доступ із мобільних застосунків.' },
      { q: 'Аналітика?', a: 'Периметр, лінії перетину, підрахунок людей, маски та каски, інтеграція з СКУД та пожежною сигналізацією.' }
    ]
  },
  {
    id: 'systems-acs',
    icon: 'acs',
    title: 'Контроль доступу (СКУД)',
    items: [
      { q: 'Які ідентифікатори підтримує система?', a: 'RFID/MIFARE, BLE/NFC, біометрія (відбиток/обличчя). Тимчасові QR-пропуски для гостей.' },
      { q: 'Інтеграції?', a: 'З відеоаналітикою, турнікетами, воротами, обліком робочого часу, 1С/ERP.' },
      { q: 'Безпека та резерв?', a: 'Контролери з пам’яттю офлайн, джерела безперебійного живлення, журнал подій, рольові права.' }
    ]
  },
  {
    id: 'systems-gates',
    icon: 'gates',
    title: 'Ворота та автоматика',
    items: [
      { q: 'Які типи воріт можете поставити?', a: 'Розпашні, відкатні, секційні, швидкісні ПВХ. Індустріальна автоматика, петлі, ловителі, каретки.' },
      { q: 'Управління?', a: 'Пульти, кнопки, фотоелементи, радар, RFID; інтеграція зі СКУД, розклад та сценарії.' },
      { q: 'Сервіс та регламент?', a: 'ТО за регламентом виробника, змащення, калібрування, заміна роликів та ущільнень.' }
    ]
  },
  {
    id: 'systems-electro',
    icon: 'electro',
    title: 'Електромонтаж',
    items: [
      { q: 'Проєктування та щити?', a: 'Однолінійні схеми, підбір апаратури, складання щитів, маркування, протоколи випробувань.' },
      { q: 'Кабельні траси?', a: 'Лотки/короби/гофра, заземлення, розділення силових та слаботочних ліній.' },
      { q: 'Пусконалагодження?', a: 'Перевірки, вимірювання, документація, інструктаж персоналу.' }
    ]
  },
  {
    id: 'systems-water',
    icon: 'water',
    title: 'Водопостачання та опалення',
    items: [
      { q: 'Матеріали трубопроводів?', a: 'PPR/PEX, мідь, чорний метал, нерж. сталь; підбір під тиск/температуру/середовище.' },
      { q: 'Котельні та автоматика?', a: 'Котли, насоси, колектори, балансування, погодне регулювання, захист і диспетчеризація.' },
      { q: 'Сервіс?', a: 'Регламентні огляди, промивка теплообмінників, діагностика витоків та повітря.' }
    ]
  },
  {
    id: 'systems-smoke',
    icon: 'smoke',
    title: 'К1/К2 димовидалення',
    items: [
      { q: 'Що входить у систему?', a: 'Димососи, клапани, шахти, протидимна вентиляція, автоматика та резервне живлення.' },
      { q: 'Норми та погодження?', a: 'Розрахунки за ДБН/ДСТУ, узгодження з ДСНС, акти випробувань.' },
      { q: 'Інтеграції?', a: 'Пожежна сигналізація, СКУД, диспетчеризація (Modbus/RS485/KNX).' }
    ]
  },
  {
    id: 'systems-vent',
    icon: 'vent',
    title: 'Вентиляція та кондиціювання',
    items: [
      { q: 'Типи систем?', a: 'Припливна/витяжна, рекуперація, локальні установки, VRF/канальні кондиціонери.' },
      { q: 'Акустика та енергоефективність?', a: 'Шумоглушники, віброізоляція, підбір вентиляторів за ККД, автоматика частотного керування.' },
      { q: 'Сервіс?', a: 'Мийка теплообмінників, фільтри, заправка фреону, перевірка дренажу.' }
    ]
  },
  {
    id: 'systems-core',
    icon: 'core',
    title: 'Алмазне свердління',
    items: [
      { q: 'Діапазон діаметрів та глибин?', a: 'Ø 32–500+ мм, глибина за узгодженням; свердління під інженерію та анкери.' },
      { q: 'Пил та вода?', a: 'З пиловідведенням/водозниженням, ізоляція робочої зони, прибирання по завершенні.' },
      { q: 'Матеріали?', a: 'З/б, цегла, камінь, граніт. За потреби — хімічне анкерування.' }
    ]
  },
  {
    id: 'contracts',
    icon: 'delivery',
    title: 'Договори, оплата та гарантія',
    items: [
      { q: 'Які умови оплати?', a: 'Передплата/етапи; безготівка з ПДВ; великі проєкти — помісячно за графіком виконання.' },
      { q: 'Гарантія?', a: 'На роботи та покриття — за договором; на обладнання — згідно з виробником. Післягарантійний сервіс є.' },
      { q: 'Як передати ТЗ/креслення?', a: 'Через форму контактів або на email info@momv.com.ua (DXF/DWG/STEP/PDF). Ми підтвердимо отримання і задаємо уточнення.' }
    ]
  }
];

// ---- утилита slug (на всякий)
const slug = (s)=> s.toLowerCase().replace(/\s+/g,'-').replace(/[^a-zа-яёіїє0-9\-]/gi,'');

// ---- генератор JSON-LD для FAQPage
const makeFaqJsonLd = (categories, filterFn)=>({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: categories.flatMap(c =>
    c.items
      .filter(filterFn || (()=>true))
      .map(it => ({
        '@type': 'Question',
        name: it.q,
        acceptedAnswer: {
          '@type': 'Answer',
          text: it.a
        }
      }))
  )
});

export default function FAQPage(){
  const meta = usePageMeta('faq');
  const [query, setQuery] = useState('');
  const [openKey, setOpenKey] = useState('');      // key формата `${cat.id}:${idx}`
  const [expandAll, setExpandAll] = useState(false);

  useEffect(()=>{
    AOS.init({ once: true, duration: 700, offset: 80, easing: 'ease-out-cubic' });
  },[]);

  // фильтр по поиску
  const q = query.trim().toLowerCase();
  const filtered = useMemo(()=>{
    if(!q) return CATEGORIES;
    const match = (s)=> s.toLowerCase().includes(q);
    return CATEGORIES.map(cat => ({
      ...cat,
      items: cat.items.filter(it => match(it.q) || match(it.a))
    })).filter(cat => cat.items.length > 0);
  },[q]);

  // JSON-LD (только видимые элементы после фильтра)
  const jsonLd = useMemo(()=> JSON.stringify(makeFaqJsonLd(filtered)), [filtered]);

  // вспомогательная функция ключа вопроса
  const keyFor = (catId, idx)=> `${catId}:${idx}`;

  return (
    <>
      <SeoMeta
        title={meta.title}
        description={meta.description}
        keywords={meta.keywords}
        image={meta.image}
        path={meta.path || '/info/faq'}
      />
      <Head>
        <div className="title-wrap">
          <div className="kicker">Підтримка</div>
          <h1>Питання та відповіді (FAQ)</h1>
          <p className="lead">Зібрали типові питання по виробництву, меблям/HPL та інженерним системам. Якщо потрібна консультація — напишіть нам.</p>
        </div>
        <div>
          <Link to="/contacts" className="btn btn--primary">Задати запитання</Link>
        </div>
      </Head>

      <Page>
        <div className="container">
          {/* Sidebar */}
          <Sidebar>
            <div style={{display:'grid', gap:8}}>
              {CATEGORIES.map(cat=>(
                <NavLink key={cat.id} href={`#${cat.id}`} title={cat.title}>
                  <span className="dot" />
                  <span style={{display:'inline-flex', alignItems:'center', gap:8}}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><Icon name={cat.icon}/></svg>
                    {cat.title}
                  </span>
                </NavLink>
              ))}
            </div>
          </Sidebar>

          {/* Content */}
          <div>
            <Toolbar>
              <input
                type="search"
                placeholder="Пошук питання… (напр. “допуски”, “СКУД”, “HPL”)"
                value={query}
                onChange={e=>setQuery(e.target.value)}
                aria-label="Пошук по FAQ"
              />
              <button className="btn" onClick={()=>setExpandAll(v=>!v)}>
                {expandAll ? 'Згорнути всі' : 'Розгорнути всі'}
              </button>
              <Badge title="Швидкі відповіді">FAQ • оновлюється</Badge>
            </Toolbar>

            {filtered.length === 0 && (
              <div style={{padding:'18px', border:'1px dashed var(--border)', borderRadius:12}}>
                Нічого не знайдено за запитом «{query}». <Link to="/contacts">Запитайте нас</Link> — підкажемо швидко.
              </div>
            )}

            {filtered.map(cat=>(
              <Section id={cat.id} key={cat.id} data-aos="fade-up">
                <div className="kicker">Розділ</div>
                <h2 style={{display:'flex', alignItems:'center', gap:10}}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><Icon name={cat.icon}/></svg>
                  {cat.title}
                </h2>

                <FaqGrid>
                  {cat.items.map((it, idx)=>{
                    const k = keyFor(cat.id, idx);
                    const isOpen = expandAll || openKey === k;
                    return (
                      <Q key={k} itemScope itemType="https://schema.org/Question" data-aos="fade-up">
                        <button className="btn" aria-expanded={isOpen} onClick={()=> setOpenKey(isOpen ? '' : k)}>
                          <span itemProp="name">{it.q}</span>
                          <span>{isOpen ? '−' : '+'}</span>
                        </button>
                        {isOpen && (
                          <div className="a" itemProp="acceptedAnswer" itemScope itemType="https://schema.org/Answer">
                            <div itemProp="text">{it.a}</div>
                          </div>
                        )}
                      </Q>
                    );
                  })}
                </FaqGrid>
              </Section>
            ))}

            {/* Информационные статьи/заметки после FAQ — для SEO и помощи пользователю */}
            <Section id="articles" data-aos="fade-up">
              <div className="kicker">Корисні матеріали</div>
              <h2>Статті та гіди</h2>
              <div style={{display:'grid', gridTemplateColumns:'repeat(3, minmax(0,1fr))', gap:12}}>
                {[
                  {
                    to:'/articles/hpl-vs-mdf',
                    title:'HPL vs MDF: що обрати для комерційних інтер’єрів',
                    lead:'Порівнюємо довговічність, ціну та стійкість до зносу'
                  },
                  {
                    to:'/articles/cctv-analytics',
                    title:'Аналітика у відеоспостереженні 2025',
                    lead:'Розпізнавання, периметр, інтеграції зі СКУД і пожежною'
                  },
                  {
                    to:'/articles/steel-grades',
                    title:'Марки сталей і термообробка',
                    lead:'Як обрати матеріал під навантаження та середовище'
                  }
                ].map((a,i)=>(
                  <article key={i} style={{
                    border:'1px solid var(--border)', background:'var(--surface)',
                    borderRadius:12, padding:14
                  }}>
                    <h3 style={{margin:'0 0 6px'}}><Link to={a.to}>{a.title}</Link></h3>
                    <p style={{margin:0, color:'var(--textMuted)'}}>{a.lead}</p>
                    <div style={{marginTop:10}}>
                      <Link className="btn btn--ghost" to={a.to}>Читати</Link>
                    </div>
                  </article>
                ))}
              </div>
            </Section>
          </div>
        </div>
      </Page>

      {/* JSON-LD для SEO */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{__html: jsonLd}} />
    </>
  );
}
